import { CategoryQuestions } from '../types';

export const memoryQuestions: CategoryQuestions = {
  title: 'Personalize Your Memory Enhancement',
  questions: {
    preferredStyle: {
      label: 'What type of mental exercises do you prefer?',
      type: 'radio',
      options: ['Memory games and puzzles', 'Visualization techniques', 'Repetition exercises', 'Mindfulness for focus']
    },
    primaryGoal: {
      label: 'What do you want to improve?',
      type: 'radio',
      options: ['Memory recall', 'Focus and attention', 'Learning ability', 'Mental clarity']
    },
    specificFocus: {
      label: 'Which memory, subject, or skill do you want clearer recall for?',
      type: 'textarea',
      placeholder: 'Optional - leave blank for general session'
    },
    useVisualMemoryAids: {
      label: 'Would you like visual memory aids during this session?',
      type: 'radio',
      options: ['Yes - Use visual aids', 'No - Audio only']
    },
    memoryAidType: {
      label: 'What type of visual memory aids would help?',
      type: 'radio',
      options: [
        'Select from my photo library',
        'Generate AI scenes from my description',
        'Both - use my photos AND generate AI scenes'
      ],
      conditional: {
        field: 'useVisualMemoryAids',
        value: 'Yes - Use visual aids'
      }
    },
    memoryPhotoSelection: {
      label: 'Select 1-2 photos from your library',
      type: 'photo-picker',
      maxPhotos: 2,
      placeholder: 'Choose meaningful photos to display during your session',
      conditional: {
        field: 'memoryAidType',
        value: 'photo library'
      }
    },
    memorySceneDescription: {
      label: 'Describe 2 memorable scenes you\'d like to visualize',
      type: 'textarea',
      placeholder: 'Example: "My childhood home with the oak tree in front" or "The beach where I got married"',
      conditional: {
        field: 'memoryAidType',
        value: 'Generate AI scenes'
      }
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for you?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  // Practitioner perspective questions
  practitionerTitle: 'Personalize Memory Enhancement for Your Client',
  practitionerQuestions: {
    preferredStyle: {
      label: 'What type of mental exercises does your client prefer?',
      type: 'radio',
      options: ['Memory games and puzzles', 'Visualization techniques', 'Repetition exercises', 'Mindfulness for focus']
    },
    primaryGoal: {
      label: 'What does your client want to improve?',
      type: 'radio',
      options: ['Memory recall', 'Focus and attention', 'Learning ability', 'Mental clarity']
    },
    specificFocus: {
      label: 'Which memory, subject, or skill does your client want clearer recall for?',
      type: 'textarea',
      placeholder: 'Optional - leave blank for general session'
    },
    useVisualMemoryAids: {
      label: 'Would your client benefit from visual memory aids during this session?',
      type: 'radio',
      options: ['Yes - Use visual aids', 'No - Audio only']
    },
    memoryAidType: {
      label: 'What type of visual memory aids would help your client?',
      type: 'radio',
      options: [
        'Select from my photo library',
        'Generate AI scenes from description',
        'Both - use my photos AND generate AI scenes'
      ],
      conditional: {
        field: 'useVisualMemoryAids',
        value: 'Yes - Use visual aids'
      }
    },
    memoryPhotoSelection: {
      label: 'Select 1-2 photos from your library for your client',
      type: 'photo-picker',
      maxPhotos: 2,
      placeholder: 'Choose meaningful photos to display during the session',
      conditional: {
        field: 'memoryAidType',
        value: 'photo library'
      }
    },
    memorySceneDescription: {
      label: 'Describe 2 memorable scenes for your client to visualize',
      type: 'textarea',
      placeholder: 'Example: "Client\'s childhood home" or "A meaningful place from their past"',
      conditional: {
        field: 'memoryAidType',
        value: 'Generate AI scenes'
      }
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your client?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    }
  },
  practitionerGroupTitle: 'Personalize Memory Enhancement for Your Clients',
  practitionerGroupQuestions: {
    groupContext: {
      label: 'What type of group memory activity are you creating?',
      type: 'radio',
      options: ['Study group session', 'Team training/learning', 'Family memory exercises', 'Classroom activity', 'Senior group cognitive wellness', 'Memory workshop']
    },
    collectiveSkillLevel: {
      label: 'What is the overall cognitive/memory skill level of the group?',
      type: 'radio',
      options: ['Mixed abilities - need accommodation', 'Similar beginner level', 'Similar intermediate level', 'Similar advanced level']
    },
    groupMemoryGoal: {
      label: 'What does the group want to achieve together?',
      type: 'textarea',
      placeholder: 'Describe what the group wants to learn, remember, or improve...'
    },
    exerciseStyle: {
      label: 'What type of group memory exercises work best?',
      type: 'radio',
      options: ['Collaborative memory games', 'Individual practice with group sharing', 'Partner/pair exercises', 'Competitive memory challenges', 'Supportive group repetition']
    },
    groupDynamics: {
      label: 'How should the session accommodate the group?',
      type: 'radio',
      options: ['Everyone participates equally', 'Allow for different paces', 'Pair stronger with weaker participants', 'Create supportive team environment']
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your clients?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  groupTitle: 'Personalize Your Group Memory Enhancement',
  groupQuestions: {
    groupContext: {
      label: 'What type of group memory activity are you creating?',
      type: 'radio',
      options: ['Study group session', 'Team training/learning', 'Family memory exercises', 'Classroom activity', 'Senior group cognitive wellness', 'Memory workshop']
    },
    collectiveSkillLevel: {
      label: 'What is the overall cognitive/memory skill level of your group?',
      type: 'radio',
      options: ['Mixed abilities - need accommodation', 'Similar beginner level', 'Similar intermediate level', 'Similar advanced level']
    },
    groupMemoryGoal: {
      label: 'What does your group want to achieve together?',
      type: 'textarea',
      placeholder: 'Describe what the group wants to learn, remember, or improve...'
    },
    exerciseStyle: {
      label: 'What type of group memory exercises work best?',
      type: 'radio',
      options: ['Collaborative memory games', 'Individual practice with group sharing', 'Partner/pair exercises', 'Competitive memory challenges', 'Supportive group repetition']
    },
    groupDynamics: {
      label: 'How should the session accommodate your group?',
      type: 'radio',
      options: ['Everyone participates equally', 'Allow for different paces', 'Pair stronger with weaker participants', 'Create supportive team environment']
    },
    useVisualMemoryAids: {
      label: 'Would visual memory aids help your group during this session?',
      type: 'radio',
      options: ['Yes - Use visual aids', 'No - Audio only']
    },
    memoryAidType: {
      label: 'What type of visual memory aids would help your group?',
      type: 'radio',
      options: [
        'Select from my photo library',
        'Generate AI scenes from description',
        'Both - use my photos AND generate AI scenes'
      ],
      conditional: {
        field: 'useVisualMemoryAids',
        value: 'Yes - Use visual aids'
      }
    },
    memoryPhotoSelection: {
      label: 'Select 1-2 photos from your library for the group',
      type: 'photo-picker',
      maxPhotos: 2,
      placeholder: 'Choose meaningful photos to display during the group session',
      conditional: {
        field: 'memoryAidType',
        value: 'photo library'
      }
    },
    memorySceneDescription: {
      label: 'Describe 2 memorable scenes for the group to visualize',
      type: 'textarea',
      placeholder: 'Example: "The school where we all met" or "Our team\'s favorite training location"',
      conditional: {
        field: 'memoryAidType',
        value: 'Generate AI scenes'
      }
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your group?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  giftTitle: 'Personalize Memory Enhancement for Your Loved One',
  giftQuestions: {
    primaryGoal: {
      label: 'What memory skills do they want to improve?',
      type: 'radio',
      options: ['Names and faces', 'Important dates and numbers', 'Learning and studying', 'Daily tasks and routines', 'General sharpness']
    },
    // Using preferredStyle to match regular user questions - backend expects this field
    preferredStyle: {
      label: 'What is their current memory skill level?',
      type: 'radio',
      options: ['Beginner - just starting to work on memory', 'Intermediate - some experience', 'Advanced - looking for challenge', 'Senior/aging - cognitive wellness focus']
    },
    specificFocus: {
      label: 'What specific memory challenges are they working on?',
      type: 'textarea',
      placeholder: 'Optional - describe what they want to remember better...'
    },
    additionalNotes: {
      label: 'Anything else the AI should know to create a perfect session for them?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  }
};
